<template>
  <tr
    class="k-table-row"
    :class="[
      { 'k-table-row-selected': selected, 'k-table-row-clickable': supportRowClick },
      classFunc(row)
    ]"
    @click="handleRowClick"
    @keydown.enter="handleRowClick"
    @keydown.space="handleRowClick"
    tabindex="0"
    role="row"
    :aria-selected="selected"
  >
    <td v-for="key in columnOrder"
      :key="key"
      :class="getCellClasses(key, row[key])"
      role="cell"
    >
      <template v-if="headers[key].type === 'url' && row[key]">
        <router-link :to="row[key].path" :tag="row[key].tag">
          <div v-html="headers[key].displayAs(row[key])"></div>
        </router-link>
      </template>
      <template v-else-if="headers[key].type === 'external_url'">
        <a v-if="row[key]" :href="row[key]" target="_blank">
          <span v-html="truncatedContent[key]?.displayText || headers[key].displayAs(row[key])"></span>
          <i class="fa-solid fa-external-link-alt" aria-hidden="true"></i>
        </a>
        <span v-else> - </span>
      </template>
      <template v-else-if="headers[key].type === 'action'">
        <button
          class="btn-action-unstyled"
          @click.stop="!disabledAction(row[key]) && onActionClick(row[key], row.index, row, key)"
          :aria-label="`${key} ${row.name || 'this row'}`"
          v-html="headers[key].displayAs(row[key])"
        >
        </button>
      </template>
      <template v-else-if="headers[key].type === 'button'">
        <k-table-button
          :preset="buttonConfig[key].preset"
          :format="buttonConfig[key].format"
          :variant="buttonConfig[key].variant"
          :text="buttonConfig[key].text"
          :icon="buttonConfig[key].icon"
          :title="buttonConfig[key].title"
          :aria-label="buttonConfig[key].ariaLabel"
          :disabled="buttonConfig[key].disabled"
          :download-url="buttonConfig[key].downloadUrl"
          :custom-color="buttonConfig[key].customColor"
          :custom-fill="buttonConfig[key].customFill"
          :custom-size="buttonConfig[key].customSize"
          @button-click.stop="onButtonClick(key)"
        />
      </template>
      <template v-else-if="headers[key].type === 'function'">
        <span v-html="headers[key].displayAs(row, row.index)"></span>
      </template>
      <template v-else-if="headers[key].type === 'string'">
        <span v-html="truncatedContent[key]?.displayText || headers[key].displayAs(row[key])"></span>
        <button
          v-if="truncatedContent[key]?.hasFullTextButton"
          class="btn btn-link"
          @click="viewFullText(key)"
          :aria-describedby="`view-full-text-${row.index}-${key}`"
        >
          <i class="fa-solid fa-external-link-alt" aria-hidden="true"></i>
          See more
        </button>
        <span v-if="truncatedContent[key]?.hasFullTextButton" :id="`view-full-text-${row.index}-${key}`" class="sr-only">View full text for {{ headers[key].displayName || headers[key].name || key }}</span>
      </template>
      <template v-else-if="headers[key].type === 'pill'">
        <div class="status-cell">
          <k-status-pill
            :type="pillConfig[key].type"
            :fill="pillConfig[key].fill"
            :text-color="pillConfig[key].textColor"
            :status-value="pillConfig[key].statusValue"
          >
            <template #label v-if="pillConfig[key].slotContent">
              <div v-html="pillConfig[key].slotContent"></div>
            </template>
          </k-status-pill>
        </div>
      </template>
      <template v-else-if="headers[key].type === 'boolean'">
        <div v-if="row[key] === true" class="k-table-cell-boolean success" role="img" aria-label="True" title="True">
          <i class="fa-solid fa-check" aria-hidden="true"></i>
        </div>
        <div v-else-if="row[key] === false" class="k-table-cell-boolean error" role="img" aria-label="False" title="False">
          <i class="fa-solid fa-xmark" aria-hidden="true"></i>
        </div>
        <div v-else class="k-table-cell-boolean default" role="img" aria-label="Pending" title="Pending">
          <i class="fa-solid fa-minus" aria-hidden="true"></i>
        </div>
      </template>
      <template v-else-if="headers[key].type === 'rag-dot'">
        <k-rag-dot
          :rag-status="ragDotConfig[key].ragStatus"
          :metric-value="ragDotConfig[key].metricValue"
          :metric-type="ragDotConfig[key].metricType"
        >
          <template #label v-if="ragDotConfig[key].slotContent">
            <div v-html="ragDotConfig[key].slotContent"></div>
          </template>
        </k-rag-dot>
      </template>
      <template v-else>
        <span v-html="headers[key].displayAs(row[key])"></span>
      </template>
    </td>
  </tr>
</template>

<style scoped>
a {
  display: flex;
  align-items: center;
}

.fa-external-link-alt {
  margin-left: 0.25em;
  margin-top: 0.1em;
}

.k-table-action.clickable:hover button {
  color: var(--kate-type-link-hover);
  text-decoration: underline;
}

.k-table-row-clickable {
  cursor: pointer;
}

[data-theme="dark"] .k-table-row-clickable:hover {
  background-color: rgba(58 64 93 / 0.5) !important;
}

.k-table-row-clickable:hover {
  background-color: var(--kate-background-alt-alpha);
}

td {
  vertical-align: middle;
}

.k-table-cell-boolean.success {
  color: var(--kate-success);
}

.k-table-cell-boolean.error {
  color: var(--kate-type-danger);
}

.k-table-cell-boolean.default {
  color: var(--kate-type-primary);
}
</style>

<script>
import KStatusPill from '@base-components/k-status-pill.vue';
import KRagDot from '@base-components/k-rag-dot.vue';
import KTableButton from '@base-components/tables/display/k-table-button.vue';

export default {
  components: {
    KStatusPill,
    KRagDot,
    KTableButton,
  },
  props: {
    row: Object,
    headers: Object,
    columnOrder: Array,
    selected: Boolean,
    fixedColumnKeys: Array,
    onActionClick: Function,
    supportRowClick: {
      type: Boolean,
      default: false,
    },
    classFunc: {
      type: Function,
      default: () => '',
    },
  },

  computed: {
    truncatedContent() {
      const stringConfig = {};
      this.columnOrder.forEach(key => {
        if (this.headers[key].type === 'string' || this.headers[key].type === 'external_url') {
          const fullText = this.headers[key].displayAs(this.row[key]);
          const displayLength = this.headers[key].options.displayLength || 160;
          const isTruncated = fullText?.length > displayLength;

          stringConfig[key] = {
            displayText: isTruncated ? `${fullText.substring(0, displayLength)}... ` : fullText,
            fullText,
            hasFullTextButton: isTruncated && !this.headers[key].options.hideFullTextButton,
          };
        }
      });
      return stringConfig;
    },

    pillConfig() {
      const pillHeaders = Object.keys(this.headers).filter(key => this.headers[key].type === 'pill');
      const config = {};

      pillHeaders.forEach(key => {
        config[key] = this.getPillConfig(key);
      });

      return config;
    },

    ragDotConfig() {
      const ragDotHeaders = Object.keys(this.headers).filter(key => this.headers[key].type === 'rag-dot');
      const config = {};

      ragDotHeaders.forEach(key => {
        config[key] = this.getRagDotConfig(key);
      });

      return config;
    },

    buttonConfig() {
      const config = {};
      this.columnOrder.forEach(headerKey => {
        if (this.headers[headerKey].type === 'button') {
          const buttonOptions = this.headers[headerKey].options;
          config[headerKey] = {};
          if (buttonOptions) {
            if (typeof buttonOptions === 'function') {
              config[headerKey] = buttonOptions(this.row);
            } else {
              Object.keys(buttonOptions).forEach(optionKey => {
                const value = buttonOptions[optionKey];
                if (optionKey === 'callback' && typeof value === 'function') {
                  config[headerKey][optionKey] = value;
                } else {
                  config[headerKey][optionKey] = typeof value === 'function' ? value(this.row) : value;
                }
              });
            }
          }
          // fallback if ariaLabel is not set
          if (!config[headerKey].ariaLabel) {
            const buttonText = config[headerKey].text || config[headerKey].preset || headerKey;
            const rowContext = this.row.name || this.row.title || this.row.id || this.row.label;
            config[headerKey].ariaLabel = rowContext ? `${buttonText} ${rowContext}` : buttonText;
          }
        }
      });
      return config;
    },
  },

  methods: {
    onButtonClick(columnKey) {
      const callback = this.buttonConfig[columnKey]?.callback;
      if (typeof callback === 'function') {
        callback(this.row[columnKey], this.row.index, this.row, columnKey);
      } else {
        this.onActionClick(this.row[columnKey], this.row.index, this.row, columnKey);
      }
    },

    handleRowClick() {
      if (this.supportRowClick) {
        this.$emit('row-click', this.row);
      }
    },

    disabledAction(action) {
      return action?.disabled;
    },

    getCellClasses(key, cell) {
      const classes = [
        'k-table-cell',
        `k-table-key-${key}`,
        this.headers[key].type === 'button' ? 'k-table-row-button' : `k-table-${this.headers[key].type}`,
      ];
      if (this.fixedColumnKeys.includes(key)) {
        classes.push('fixed-column');
      }
      if (this.isCellClickable(key, cell)) {
        classes.push('clickable');
      }
      const typeClasses = this.getHeaderTypeClasses(key, cell);
      if (typeClasses) {
        classes.push(typeClasses);
      }
      return classes.join(' ');
    },

    isCellClickable(key, cell) {
      if (this.headers[key].type === 'action') {
        return !this.disabledAction(cell);
      }
      if (this.headers[key].type === 'button') {
        return !this.buttonConfig[key]?.disabled;
      }
      return false;
    },

    getHeaderTypeClasses(key, cell) {
      const header = this.headers[key];
      switch (header.type) {
        case 'rag_status':
          return this.getRagStatusRowClasses(header, cell);
        case 'percentage':
        case 'number':
          return this.getNumRowClasses(header, cell);
        case 'boolean':
          return 'k-table-cell-container';
        default:
          return '';
      }
    },

    getNumRowClasses(header, cell) {
      if (!header.options.color) return '';
      if (cell >= 70) return 'k-success';
      if (cell > 30) return 'k-warning';
      if (cell >= 0 && cell != null) return 'k-poor-score';
      return '';
    },

    getRagStatusRowClasses(header, cell) {
      if (!header.options.color) return '';
      if (cell === 'Green') return 'k-success';
      if (cell === 'Amber') return 'k-warning';
      if (cell === 'Red' && cell != null) return 'k-poor-score';
      return '';
    },

    viewFullText(key) {
      this.$emit('viewFullText', key, this.row[key]);
    },

    getConfigValue(optionValue, cellValue) {
      if (!optionValue) return '';
      return typeof optionValue === 'function' ? optionValue(cellValue) : optionValue;
    },

    getPillConfig(key) {
      const statusValue = this.headers[key].displayAs(this.row[key]);
      const headerOptions = this.headers[key].options || {};

      return {
        statusValue,
        type: this.getConfigValue(headerOptions?.type, statusValue),
        fill: this.getConfigValue(headerOptions?.fill, statusValue),
        textColor: this.getConfigValue(headerOptions?.textColor, statusValue),
        slotContent: this.getConfigValue(headerOptions?.slotContent, statusValue),
      };
    },

    getRagDotConfig(key) {
      const headerOptions = this.headers[key].options || {};
      const metricKey = headerOptions.metricField || key;
      const metricValue = this.headers[key].displayAs(this.row[metricKey]);
      // ragField: row field name containing RAG status
      // ragStatus: static value or function to compute RAG status
      const ragStatus = this.row[headerOptions.ragField] || this.getConfigValue(headerOptions?.ragStatus, metricValue);
      return {
        metricValue,
        ragStatus,
        metricType: this.getConfigValue(headerOptions?.metricType, metricValue),
        slotContent: this.getConfigValue(headerOptions?.slotContent, metricValue),
      };
    },
  },
};
</script>
